/**
 * 화면 문지기 (Todak App.jsx 의 Protected · GuestOnly 와 같은 역할).
 *
 * - SessionGate: 앱이 뜰 때 한 번 세션을 확인합니다. 확인하는 동안은 바탕색만 그립니다.
 *   이 동안 로그인 화면을 먼저 그리면, 로그인한 사람에게도 한 프레임 비쳐 깜빡입니다 (히어로챗 QA A-1)
 * - RequireMember: 로그인 전이면 /start(시작 화면), 첫 방문을 마치지 않았으면 멈춘 장면(/welcome, /counter)
 * - RequireOnboarding: 첫 방문 화면. 그 장면의 유저만 들이고, 아니면 맞는 곳으로 보냅니다
 * - GuestOnly: 로그인했으면 /start · /manual-login 을 건너뜁니다
 *
 * 어디로 보낼지는 user.onboardingStep 하나로 정합니다 (DEV_SPEC 3장).
 */
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '../stores/session.js';
import { Button } from './Button.jsx';

/**
 * 이 유저가 지금 있어야 할 첫 방문 화면. 첫 방문을 마쳤으면 null. 가입 동의(지금 판)가 없으면 동의 화면이 먼저(DEV-07r · D97),
 * 출생 연도가 없는 단골도 동의 화면에서 그것만 한 번(DEV-07z · D99)
 */
export function onboardingPathOf(user) {
    if (user?.needsConsent) {
        return '/consent';
    }
    if (!user || user.onboardingStep === 'done') {
        return null;
    }
    return user.onboardingStep === 'welcome' ? '/welcome' : '/counter';
}

/** 로그인 뒤 처음 갈 곳 */
export function landingPathOf(user, returnTo = '/') {
    return onboardingPathOf(user) ?? returnTo;
}

export function SessionGate({ children }) {
    const status = useSession((state) => state.status);
    const restore = useSession((state) => state.restore);

    useEffect(() => {
        if (status === 'checking') {
            restore();
        }
        // 앱이 뜰 때 한 번만 묻습니다. status 가 바뀔 때마다 다시 묻지 않습니다.
    }, []);

    if (status === 'checking') {
        return <div className="min-h-dvh bg-bg" aria-busy="true" />;
    }
    if (status === 'error') {
        return (
            <main className="flex min-h-dvh flex-col items-center justify-center gap-4 px-gutter text-center">
                <p className="m-0 text-body">인생상회에 닿지 못했어요. 연결을 확인하고 다시 시도해 주세요.</p>
                <Button variant="secondary" size="md" onClick={() => restore()}>
                    다시 시도하기
                </Button>
            </main>
        );
    }
    return children;
}

export function RequireMember() {
    const status = useSession((state) => state.status);
    const user = useSession((state) => state.user);
    const location = useLocation();

    if (status !== 'member' || !user) {
        // 로그인하지 않았으면 시작 화면(L0, 시안 04)
        return <Navigate to="/start" replace state={{ from: location.pathname }} />;
    }
    const onboardingPath = onboardingPathOf(user);
    if (onboardingPath) {
        return <Navigate to={onboardingPath} replace />;
    }
    return <Outlet />;
}

/** @param {{ path: '/consent'|'/welcome'|'/counter' }} props 이 화면의 경로 */
export function RequireOnboarding({ path }) {
    const status = useSession((state) => state.status);
    const user = useSession((state) => state.user);
    const location = useLocation();

    if (status !== 'member' || !user) {
        return <Navigate to="/start" replace />;
    }
    const target = landingPathOf(user);
    // 카운터에서 나온 골목 쉼(/welcome?paused=1, 핸드오프 15 X3)은 카운터 단계 단골도 들임
    const pausedAlley =
        path === '/welcome' && target === '/counter' && new URLSearchParams(location.search).get('paused') === '1';
    if (target !== path && !pausedAlley) {
        return <Navigate to={target} replace />;
    }
    return <Outlet />;
}

export function GuestOnly() {
    const status = useSession((state) => state.status);
    const user = useSession((state) => state.user);

    if (status === 'member' && user) {
        return <Navigate to={landingPathOf(user)} replace />;
    }
    return <Outlet />;
}
